import useSWR from "swr";
import { swrKey } from "@/lib/swr";
import { RouteTabs } from "@/components/nav/route-tabs";
import { Outlet, useLocation, useParams } from "react-router-dom";
import Page403 from "@/components/zthd/403";
import CreatePrd from "@/screens/Prds/Create";
import CreateVersion from "@/screens/Versions/Create";
import { AppHeader, AppOwnerAvatar } from "./app-header";
import { LocalDirButton } from "@/components/prds/exec-menu";
import { CreateReportDialog } from "./create-report-dialog";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { countSuffix } from "@/lib/utils";
import { ListLoading } from "@/components/list/status-text";
import { useApp, useAppCatalog } from "@/hooks/apps";
import type { Report, WishPoolResult } from "@/types/domain";
import type { RcStats } from "@/constants/rc";


// Totals for the tab labels — only tabs whose count means outstanding work
// (my pending Prds, open bugs, reports, RCs). Archive/inventory tabs (Versions,
// Channels) stay unnumbered: their totals are history sizes, not work
// signals. Cheap pageSize-1 queries.
const useTabCounts = (id?: string, appKey?: string, hidden?: Set<string>) => {
  const one = { pageIndex: 0, pageSize: 1 };
  // Prds = my Not Started + In Process on this App (WED-237). Same key as the Prds tab's
  // default view — SWR dedupes, and completion changes revalidate it.
  const { data: wishPool } = useSWR<WishPoolResult>(
    appKey
      ? swrKey("/engagements/wish-pool", { appId: appKey, scope: "me", pageIndex: 0, pageSize: 25 })
      : null
  );
  // Hidden tabs don't render a label — skip their count fetches too.
  const { data: reports } = useSWR<{ data: Report[]; total: number }>(
    id && !hidden?.has("reports") ? swrKey("/reports", { pid: id, ...one }) : null
  );
  const { data: rcStats } = useSWR<RcStats>(
    id && !hidden?.has("rcs") ? swrKey("/remote-configs/stats", { pid: id }) : null
  );
  // Same key the Rollout tab queries by — SWR dedupes.
  const { data: rolloutTasks } = useSWR<{ confirmedAt?: string | null }[]>(
    id && !hidden?.has("rollout") ? swrKey("/rollout-tasks", { pid: id }) : null
  );
  return {
    wishPool: wishPool?.total,
    reports: reports?.total,
    rcs: rcStats?.total,
    // Pending rollout confirmations only — the tab tracks outstanding work.
    rollout: rolloutTasks
      ? rolloutTasks.filter((t) => !t.confirmedAt).length
      : undefined,
  };
};

// Prds (wish-pool route) shows this app's engagements by my status, plus the
// whole App; version rhythm lives in Versions, archive data in Reports.
const AppLayout = () => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const { isLoading } = useAppCatalog();
  const app = useApp(id);
  // Same key the wish-pool tab queries by (app code preferred over id).
  const appKey = app ? String(app.code ?? app.id) : undefined;
  // Per-app tab switches (Settings → Tabs): hidden keys drop the tab and its
  // count fetch. UI-only — direct URLs still work, permissions unchanged.
  const hidden = new Set(app?.hiddenTabs ?? []);
  const counts = useTabCounts(id, appKey, hidden);
  const base = `/apps/${id}`;
  // Issues first — it's the app's default landing tab (/apps/:id redirects
  // there, sidebar included): the relation buckets are the working view.
  // key = the HIDEABLE_TABS handle; keyless tabs (Prds/Versions/Settings)
  // are core navigation and can't be switched off.
  const roster = [
    // Labeled "Prds" (owner 2026-08-18): the team reads these as PRDs, even
    // though the pool technically mixes prd/bug/doc issues.
    { name: `Prds${countSuffix(counts.wishPool)}`, to: `${base}/wish-pool` },
    // Doc-type issues live here, out of the Prds buckets — reference
    // material, so unnumbered (not a work signal, same rule as Versions).
    { key: "docs", name: "Docs", to: `${base}/docs` },
    { name: "Versions", to: `${base}/versions` },
    {
      key: "reports",
      name: `Reports${countSuffix(counts.reports)}`,
      to: `${base}/reports`,
    },
    // Bugs tab retired (issue unification): bug issues live in the Issues
    // state buckets with a red Bug chip; /apps/:id/bugs redirects there.
    // Rollout confirmation tasks (auto-created on rollout/online) — count is
    // pending sign-offs, the backlog expected to stay at zero.
    {
      key: "rollout",
      name: `Rollout${countSuffix(counts.rollout)}`,
      to: `${base}/rollout-tasks`,
    },
    // 渠道包只有少数 app 用(如 JPSingle),用不上的 app 在 Settings 关掉。
    { key: "channels", name: "Channels", to: `${base}/channels` },
    // Cross-version build artifacts inventory (unnumbered: history size, not
    // a work signal — same rule as Versions/Channels).
    { key: "artifacts", name: "Artifacts", to: `${base}/artifacts` },
    // RC config switches (A/B tests / deploys / ops adjustments) for this app.
    { key: "rcs", name: `RC${countSuffix(counts.rcs)}`, to: `${base}/rcs` },
    // Ownership + visibility members; actions inside are gated to owner/admin.
    { name: "Settings", to: `${base}/settings` },
  ];
  const items = roster
    .filter((t) => !t.key || !hidden.has(t.key))
    .map(({ name, to }) => ({ name, to }));
  // The full scoped catalog is the client's visibility source (server enforces the
  // real check): an app id outside it → 403 page (internal system, existence
  // isn't a secret), not a broken shell. While the catalog is in flight, render the
  // shell (tabs + Outlet) anyway so the child's data request fires in
  // parallel instead of waterfalling behind it; children only need the
  // URL id, and the server scopes every child query on its own.
  if (!isLoading && !app) return <Page403 />;

  // The create entry follows the active tab (owner 2026-09-12) and sits in
  // the tab bar's action slot — one button instead of three in the header
  // (phones: beside the tab Select). Prds/Docs share CreatePrd (prd / bug /
  // doc is a radio inside the dialog; version-scoped bug submission stays on
  // the version pages); RC has its own New RC inside the tab; the rest
  // create nothing here.
  const section = pathname.slice(base.length + 1).split("/")[0];
  const createAction =
    section === "wish-pool" || section === "docs" ? (
      <CreatePrd appId={String(id)} />
    ) : section === "versions" ? (
      <CreateVersion pid={String(id)} />
    ) : section === "reports" ? (
      <CreateReportDialog pid={String(id)} />
    ) : undefined;

  return (
    <div className="flex flex-col gap-4">
      <PageBreadcrumb parent={{ label: "Apps" }} current={app?.name ?? "…"} />
      {/* Header row: app identity left; local exec dir + owner avatar right. */}
      <div className="flex flex-wrap items-end justify-between gap-2">
        {app ? <AppHeader app={app} /> : <ListLoading />}
        {app && (
          <div className="flex items-center gap-3">
            <LocalDirButton appId={String(app.id)} />
            <AppOwnerAvatar app={app} />
          </div>
        )}
      </div>
      <RouteTabs items={items} action={createAction} />
      <Outlet />
    </div>
  );
};

export default AppLayout;
